import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import logoLoading from "../../../assets/img/logoEmpresaLoading.gif";
import { requestChatbotSession } from "../../../services/portalAluno/chatbot/chatbotService";
import {
  getChatbotContentDetailsPath,
  getChatbotContentIdFromMessage,
  getChatbotContentIdFromUrl,
  isTrustedChatbotContentOrigin,
} from "../../../utils/portalAluno/chatbotContentLink";
import { buildChatbotUrl } from "../../../utils/portalAluno/chatbotUrl";
import { ApplicationState } from "../../../store";
import "./styles.css";

type ChatbotState =
  | { status: "authorizing" }
  | { status: "loading"; url: string }
  | { status: "ready"; url: string }
  | { status: "error"; title: string; message: string };

const getErrorContent = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;

  if (status === 401) {
    return {
      title: "Sessão expirada",
      message: "Entre novamente no Conectado para acessar o ChatBot.",
    };
  }

  if (status === 403) {
    return {
      title: "ChatBot indisponível",
      message:
        "O ChatBot ainda não está disponível para o seu perfil e cidade.",
    };
  }

  if (status === 422) {
    return {
      title: "Cadastro incompleto",
      message:
        "Seu usuário precisa ter perfil e cidade vinculados para acessar o ChatBot.",
    };
  }

  const message = error instanceof Error ? error.message : "";

  return {
    title: "Não foi possível abrir o ChatBot",
    message:
      message ||
      "Verifique sua conexão e tente novamente. Se o erro continuar, procure o suporte.",
  };
};

const ChatbotPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const accessToken = useSelector(
    (state: ApplicationState) => state.auth.data?.accessToken
  );
  const frameRef = React.useRef<HTMLIFrameElement>(null);
  const isOpeningContentRef = React.useRef(false);
  const [attempt, setAttempt] = React.useState(0);
  const [state, setState] = React.useState<ChatbotState>({
    status: "authorizing",
  });
  const chatbotUrl =
    state.status === "loading" || state.status === "ready" ? state.url : null;

  React.useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;

    setState({ status: "authorizing" });

    requestChatbotSession(abortController.signal)
      .then((sessionToken) => {
        if (!isActive) return;

        setState({
          status: "loading",
          url: buildChatbotUrl(sessionToken, window.location.origin),
        });
      })
      .catch((error) => {
        if (!isActive || abortController.signal.aborted) return;

        setState({ status: "error", ...getErrorContent(error) });
      });

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [accessToken, attempt]);

  const handleOpenContent = React.useCallback(
    (contentId: number) => {
      if (isOpeningContentRef.current) return;
      isOpeningContentRef.current = true;

      const frame = frameRef.current;
      if (frame?.contentWindow) {
        try {
          const currentFrameUrl = frame.contentWindow.location.href;
          if (getChatbotContentIdFromUrl(currentFrameUrl)) {
            frame.contentWindow.history.back();

            window.setTimeout(() => {
              try {
                const pendingUrl = frame.contentWindow?.location.href;
                if (
                  chatbotUrl &&
                  pendingUrl &&
                  getChatbotContentIdFromUrl(pendingUrl)
                ) {
                  frame.src = chatbotUrl;
                }
              } catch {
              }
            }, 500);
          }
        } catch {
        }
      }

      navigate(getChatbotContentDetailsPath(contentId), {
        state: {
          contentType: "conteudos",
          from: "/app/chatbot",
          fromKey: location.key,
        },
      });

      window.setTimeout(() => {
        isOpeningContentRef.current = false;
      }, 0);
    },
    [chatbotUrl, location.key, navigate]
  );

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;

      const chatbotOrigin = chatbotUrl
        ? new URL(chatbotUrl).origin
        : undefined;
      if (
        !isTrustedChatbotContentOrigin(event.origin) &&
        event.origin !== chatbotOrigin
      ) {
        return;
      }

      const contentId = getChatbotContentIdFromMessage(event.data);
      if (contentId) handleOpenContent(contentId);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [chatbotUrl, handleOpenContent]);

  const handleFrameLoad = () => {
    try {
      const loadedUrl = frameRef.current?.contentWindow?.location.href;
      const contentId = loadedUrl
        ? getChatbotContentIdFromUrl(loadedUrl)
        : undefined;

      if (contentId) {
        handleOpenContent(contentId);
        return;
      }
    } catch {}

    setState((currentState) =>
      currentState.status === "loading"
        ? { status: "ready", url: currentState.url }
        : currentState
    );
  };

  const handleFrameError = () => {
    setState({
      status: "error",
      title: "Falha ao carregar o ChatBot",
      message:
        "O serviço do ChatBot não respondeu. Verifique sua conexão e tente novamente.",
    });
  };

  const isLoading = state.status === "authorizing" || state.status === "loading";

  return (
    <section className="chatbot-page" aria-label="ChatBot Conectado">
      {chatbotUrl && (
        <iframe
          ref={frameRef}
          key={`${attempt}-${chatbotUrl}`}
          className="chatbot-page__frame"
          src={chatbotUrl}
          title="ChatBot Conectado"
          allow="microphone; camera; autoplay; clipboard-write"
          referrerPolicy="no-referrer"
          onLoad={handleFrameLoad}
          onError={handleFrameError}
        />
      )}

      {isLoading && (
        <div
          className="chatbot-page__status"
          role="status"
          aria-live="polite"
        >
          <img src={logoLoading} alt="" aria-hidden="true" />
        </div>
      )}

      {state.status === "error" && (
        <div className="chatbot-page__status chatbot-page__status--error" role="alert">
          <span className="chatbot-page__error-icon" aria-hidden="true">
            !
          </span>
          <h1>{state.title}</h1>
          <p>{state.message}</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>
            Tentar novamente
          </button>
        </div>
      )}
    </section>
  );
};

export default ChatbotPage;
